import dataModel from '../models/DataModel';
import ReduxConstants from '../constants/ReduxConstants';

const STORE_VERSION = 2;
const STORE_EXPIRY = 2 * 24 * 60 * 60 * 1000; // in milliseconds please

export default class StoreManager {
  
  static get version() {
    return STORE_VERSION;
  }

  static get defaultExpiry() {
    return STORE_EXPIRY;
  }

  constructor(store) {
    this.store = store;
    this._expiry = StoreManager.defaultExpiry;
    this._lastClearDate = null;
  }

  get stateTree() {
    return this.store.getState();
  }

  set expiry(expiry) {
    this._expiry = expiry;
  }

  get expiry() {
    return this._expiry;
  }

  setUserAction(user_id){
    return {
      type: 'MAIN_SET_USER',
      user_id,
    };
  }

  setVersionAction(store_version) {
    return {
      type: 'MAIN_SET_VERSION',
      store_version,
    };
  }

  setUserId(user_id) {
    this.store.dispatch(this.setUserAction(user_id));
  }

  getUserId() {
    return this.stateTree.new_main.user_id;
  }

  getCurrentCompanyId() {
    return this.stateTree.new_companies.currentCompany ? this.stateTree.new_companies.currentCompany.id : null;
  }

  setVersion(store_version) {
    this.store.dispatch(this.setVersionAction(store_version));
  }

  getVersion() {
    return this.stateTree.new_main.store_version;
  }

  getLastClearDate() {
    return this._lastClearDate;
  }

  reset(sessionUserId, forceClear, options = {}) {
    const storeUserId = this.getUserId();
    const storeVersion = this.getVersion();
    if (!sessionUserId) {
      this.clearState(options);
      this.setUserAction(null);
      this.setVersion(StoreManager.version);
    }
    else if (options.clearAll || storeUserId != sessionUserId || storeVersion != StoreManager.version) {
      options.clearAll = true;
      options.newUser = storeUserId != sessionUserId;
      this.clearState(options);
      this.setUserId(sessionUserId);
      this.setVersion(StoreManager.version);
    }
    else if (sessionUserId == storeUserId && options.newCompany) {
      this.clearState(options);
    }
    else if (forceClear) {
      this.clearState(options);
    }
  }

  /*
   * Removes all the requested objects from the redux store (and
   * consequently any persisted parts)
   *
   * Default behavior is to clear all store objects except for those
   * referenced by the offline queue collection.
   *
   * Option properties supported:
   *    ignoreQueue - if true all objects in the offline queue will be
   *                  left alone (default is true)
   *    ignoreCollections - if true all objects in the various recognized
   *                        collection objects will be left alone (default
   *                        is true)
   *    ignoreExpired - if true all objects which have expired will be 
   *                    left alone (default is false)
   *    ignoreAssociated - if true all object associations for objects that
   *                       to be left alone will be added to the left alone
   *                       list (default is true)
   *    ignorePageState - if true the page state store will be left alone (default is true)
   *    expiry - the amount of time elapsed before an object is considered
   *             to be expired (default is STORE_EXPIRY)
   *    clearDeprecated - if true the "old" (soon to be deprecated) store
   *                      will be reset (default is true)
   *    clearAll - if true all objects will be removed and all other options
   *               are ignored (default is false)
   *    newUser - if true store representing logged in user will be cleared
   *    
   */
  clearState(options) {
    this._lastClearDate = Date.now();
    let clearOptions = Object.assign({
      ignoreQueue: true, 
      ignoreCollections: true, 
      ignoreExpired: false, 
      ignoreAssociated: true, 
      ignorePageState: true,
      clearDeprecated: true, 
      clearAll: false, 
      newUser: false,
      expiry: this._expiry, toIgnore: {},
    }, options);
    if (!clearOptions.clearAll) {
      this.generateToIgnore(clearOptions);
    }
    clearOptions.expiry_now = Date.now();
    
    if (clearOptions.clearDeprecated) {
      // TODO: should these be invoking reducers?
      if (clearOptions.newUser) {
        dataModel.stateTree.new_companies = { companies: null };
        if (clearOptions.newUser) {
          dataModel.stateTree.new_users = { currentUser: null, users: null, permissions: null };
        }
      }
      dataModel.stateTree.directories = { directories: null };
      dataModel.stateTree.notifications = { notifications: null };
    }

    // Always clear bulk assignments
    if(dataModel.stateTree.new_users){
      delete dataModel.stateTree.new_users.quickAssignments;
    }

    for (let key in dataModel) {
      // Ignore the DAO class and just do the specific DAOs
      if (key.indexOf('DAO') > 0 && key != 'collectionsDAO') {
        if (key != 'companiesDAO' || clearOptions.newUser || clearOptions.newSpaVersion) {
          if (clearOptions.newSpaVersion && key == 'attachmentsDAO') {
            continue;
          }
          const dao = dataModel[key];
          if (dao.clearState) {
            dao.clearState(clearOptions);
          }
        }
      }
    }

    if (clearOptions.newSpaVersion || !clearOptions.ignoreCollections || clearOptions.clearAll) {
      if (dataModel && dataModel.collectionsDAO) {
        dataModel.collectionsDAO.clearState(clearOptions);
      }
    }
  }

  generateToIgnore(options) {
    options.toIgnore = {
      'projects': [],
      'builds': [],
      'markets': [],
      'customers': [],
      'roles': [],
      'users': options.toIgnore.users ? options.toIgnore.users : [],
      'locations': [],
      'tasks': [],
      'referenceDocuments': [],
      'uniqueDocuments': [],
      'completionCriteria': [],
      'comments': [],
      'attachments': [],
      'userTaskAssignments': [],
      'inventories': [],
      'inventoryItems': [],
      'eventHistories': [],
      'projectUsers': [],
      'companies': [],
      'userBuildAssignments': [],
      'customProjectSettings': [],
      'projectTypes': [],
      'companyCloseoutTemplates': [],
      'closeoutTemplates': [],
      'analysisProjects': [],
      'analysisCompletionCriteria': [],
      'formFields': [],
      'closeouts': [],
      'delays': [],
      'teams': [],
      'announcements': [],
    };
    if (options.ignoreQueue) {
      const offlineQueue = dataModel.offlineQueue.findCollection();
      if (offlineQueue && offlineQueue.queueItems && offlineQueue.queueItems.length > 0) {
        for (const queueItem of offlineQueue.queueItems) {
          let id = queueItem.itemInfo.id;
          // TODO: do we need to add associations to the ignore list?
          switch (queueItem.itemKey) {
          case 'attachment':
            options.toIgnore.attachments.push(id);
            this.addAssociatedToIgnore(dataModel.attachmentsDAO.findItem(id), options);
            break;
          case 'task':
            options.toIgnore.tasks.push(id);
            this.addAssociatedToIgnore(dataModel.tasksDAO.findItem(id), options);
            break;
          case 'comment':
            options.toIgnore.comments.push(id);
            this.addAssociatedToIgnore(dataModel.commentsDAO.findItem(id), options);
            break;
          case 'project':
            options.toIgnore.projects.push(id);
            this.addAssociatedToIgnore(dataModel.projectsDAO.findItem(id), options);
            break;
          case 'completionCriterion':
            options.toIgnore.completionCriteria.push(id);
            this.addAssociatedToIgnore(dataModel.completionCriteriaDAO.findItem(id), options);
            break;
          case 'inventoryItem':
            options.toIgnore.inventoryItems.push(id);
            this.addAssociatedToIgnore(dataModel.inventoryItemsDAO.findItem(id), options);
            break;
          default:
            break;
          }
        }
      }
    }
    if (options.ignoreCollections) {
      const collections = dataModel.collectionsDAO.getCollections();
      for (const key in collections) {
        if (collections.hasOwnProperty(key)) {
          const collection = collections[key];
          if (collection && collection.collectionType) {
            switch (collection.collectionType) {
            case ReduxConstants.PROJECT:
            case ReduxConstants.FAVORITE:
              this.addCollectionToIgnore(options.toIgnore.projects, collection, options);
              break;
            case ReduxConstants.TASK:
              this.addCollectionToIgnore(options.toIgnore.tasks, collection, options);
              break;
            case ReduxConstants.INVENTORY_ITEM:
              this.addCollectionToIgnore(options.toIgnore.inventoryItems, collection, options);
              break;
            case ReduxConstants.COMMENT:
              this.addCollectionToIgnore(options.toIgnore.comments, collection, options);
              break;
            case ReduxConstants.COMPLETION_CRITERION:
              this.addCollectionToIgnore(options.toIgnore.completionCriteria, collection, options);
              break;
            case ReduxConstants.PROJECT_TYPE:
              this.addCollectionToIgnore(options.toIgnore.projectTypes, collection, options);
              break;
            case ReduxConstants.MARKET:
              this.addCollectionToIgnore(options.toIgnore.markets, collection, options);
              break;
            case ReduxConstants.CUSTOMER:
              this.addCollectionToIgnore(options.toIgnore.customers, collection, options);
              break;
            case ReduxConstants.USER:
              this.addCollectionToIgnore(options.toIgnore.users, collection, options);
              break;
            case ReduxConstants.EVENT_HISTORY:
              this.addCollectionToIgnore(options.toIgnore.eventHistories, collection, options);
              break;
            case ReduxConstants.LOCATION:
              this.addCollectionToIgnore(options.toIgnore.locations, collection, options);
              break;
            default:
              break;
            }
          }
        }
      }
    }
  }

  addCollectionToIgnore(idsToIgnore, collection, options) {
    if (collection.ids) {
      for (let id of collection.ids) {
        idsToIgnore.push(id);
        if (collection.collectionType) {
          switch (collection.collectionType) {
          case ReduxConstants.PROJECT:
          case ReduxConstants.FAVORITE:
            this.addAssociatedToIgnore(dataModel.projectsDAO.findItem(id), options);
            break;
          case ReduxConstants.TASK:
            this.addAssociatedToIgnore(dataModel.tasksDAO.findItem(id), options);
            break;
          case ReduxConstants.COMPLETION_CRITERION:
            this.addAssociatedToIgnore(dataModel.completionCriteriaDAO.findItem(id), options);
            break;
          case ReduxConstants.COMPANY:
            this.addAssociatedToIgnore(dataModel.companiesDAO.findItem(id), options);
            break;
          default:
            break;
          }
        }
      }
    }
  }

  addAssociatedToIgnore(object, options) {
    if (options.ignoreAssociated && object) {
      for (let key of ['build_id', 'root_build_id', 'builds']) {
        if (key in object) {
          let ids = object[key];
          ids = !Array.isArray(ids) ? [ids] : ids;
          for (const id of ids) {
            if (!options.toIgnore.builds.includes(id)) {
              options.toIgnore.builds.push(id);
              this.addAssociatedToIgnore(dataModel.buildsDAO.findItem(id), options);
            }
          }
        }
      }
      if (object.company_id) {
        if (!options.toIgnore.companies.includes(object.company_id)) {
          options.toIgnore.companies.push(object.company_id);
          this.addAssociatedToIgnore(dataModel.companiesDAO.findItem(object.company_id), options);
        }
      }
      if (object.location_id) {
        if (!options.toIgnore.locations.includes(object.location_id)) {
          options.toIgnore.locations.push(object.location_id);
          this.addAssociatedToIgnore(dataModel.locationsDAO.findItem(object.location_id), options);
        }
      }
      if (object.project_id) {
        if (!options.toIgnore.projects.includes(object.project_id)) {
          options.toIgnore.projects.push(object.project_id);
          this.addAssociatedToIgnore(dataModel.projectsDAO.findItem(object.project_id), options);
        }
      }
      for (let key of ['tasks', 'task_id']) {
        if (key in object) {
          let ids = object[key];
          ids = !Array.isArray(ids) ? [ids] : ids;
          for (const id of ids) {
            if (!options.toIgnore.tasks.includes(id)) {
              options.toIgnore.tasks.push(id);
              this.addAssociatedToIgnore(dataModel.tasksDAO.findItem(id), options);
            }
          }
        }
      }
      for (let key of ['users', 'project_users', 'location_users', 'user_id', 'project_manager_id', 'user_who_performed_action_id']) {
        if (key in object) {
          let ids = object[key];
          ids = !Array.isArray(ids) ? [ids] : ids;
          for (const id of ids) {
            if (!options.toIgnore.users.includes(id)) {
              options.toIgnore.users.push(id);
              this.addAssociatedToIgnore(dataModel.usersDAO.findItem(id), options);
            }
          }
        }
      }
      if (object.user_task_assignments) {
        for (const id of object.user_task_assignments) {
          if (!options.toIgnore.userTaskAssignments.includes(id)) {
            options.toIgnore.userTaskAssignments.push(id);
            const uta = dataModel.userTaskAssignmentsDAO.findItem(id);
            if (uta) {
              if (!options.toIgnore.users.includes(uta.user_id)) {
                options.toIgnore.users.push(uta.user_id);
                this.addAssociatedToIgnore(dataModel.usersDAO.findItem(uta.user_id), options);
              }
            }
          }
        }
      }
      if (object.user_build_assignments) {
        for (const id of object.user_build_assignments) {
          if (!options.toIgnore.userBuildAssignments.includes(id)) {
            options.toIgnore.userBuildAssignments.push(id);
            const uba = dataModel.userBuildAssignmentsDAO.findItem(id);
            if (uba) {
              if (!options.toIgnore.users.includes(uba.user_id)) {
                options.toIgnore.users.push(uba.user_id);
                this.addAssociatedToIgnore(dataModel.usersDAO.findItem(uba.user_id), options);
              }
            }
          }
        }
      }
      if (object.custom_project_settings) {
        for (const id of object.custom_project_settings) {
          if (!options.toIgnore.customProjectSettings.includes(id)) {
            options.toIgnore.customProjectSettings.push(id);
            this.addAssociatedToIgnore(dataModel.customProjectSettingsDAO.findItem(id), options);
          }
        }
      }
      if (object.completion_criteria) {
        for (const id of object.completion_criteria) {
          if (!options.toIgnore.completionCriteria.includes(id)) {
            options.toIgnore.completionCriteria.push(id);
            this.addAssociatedToIgnore(dataModel.completionCriteriaDAO.findItem(id), options);
          }
        }
      }
      if (object.event_histories) {
        for (const id of object.event_histories) {
          if (!options.toIgnore.eventHistories.includes(id)) {
            options.toIgnore.eventHistories.push(id);
            this.addAssociatedToIgnore(dataModel.eventHistoriesDAO.findItem(id), options);
          }
        }
      }
      if (object.comments) {
        for (const id of object.comments) {
          if (!options.toIgnore.comments.includes(id)) {
            options.toIgnore.comments.push(id);
            this.addAssociatedToIgnore(dataModel.commentsDAO.findItem(id), options);
          }
        }
      }
      for (let key of ['attachments', 'attachment_id', 'documents', 'document_id', 'site_photo_id']) {
        if (key in object) {
          let ids = object[key];
          ids = !Array.isArray(ids) ? [ids] : ids;
          for (const id of ids) {
            if (!options.toIgnore.attachments.includes(id)) {
              options.toIgnore.attachments.push(id);
              this.addAssociatedToIgnore(dataModel.attachmentsDAO.findItem(id), options);
            }
          }
        }
      }
      for (let key of ['closeouts', 'closeout_id']) {
        if (key in object) {
          let ids = object[key];
          ids = !Array.isArray(ids) ? [ids] : ids;
          for (const id of ids) {
            if (!options.toIgnore.closeouts.includes(id)) {
              options.toIgnore.closeouts.push(id);
              this.addAssociatedToIgnore(dataModel.closeoutsDAO.findItem(id), options);
            }
          }
        }
      }
      if (object.form_fields) {
        for (const id of object.form_fields) {
          if (!options.toIgnore.formFields.includes(id)) {
            options.toIgnore.formFields.push(id);
            this.addAssociatedToIgnore(dataModel.formFieldsDAO.findItem(id), options);
          }
        }
      }
      if (object.market_id) {
        if (!options.toIgnore.markets.includes(object.market_id)) {
          options.toIgnore.markets.push(object.market_id);
          this.addAssociatedToIgnore(dataModel.marketsDAO.findItem(object.market_id), options);
        }
      }
      if (object.customers) {
        for (const id of object.customers) {
          if (!options.toIgnore.customers.includes(id)) {
            options.toIgnore.customers.push(id);
            this.addAssociatedToIgnore(dataModel.customersDAO.findItem(id), options);
          }
        }
      }
      if (object.inventory_items) {
        for (const id of object.inventory_items) {
          if (!options.toIgnore.inventoryItems.includes(id)) {
            options.toIgnore.inventoryItems.push(id);
            this.addAssociatedToIgnore(dataModel.inventoryItemsDAO.findItem(id), options);
          }
        }
      }
      if (object.reference_documents) {
        for (const id of object.reference_documents) {
          if (!options.toIgnore.referenceDocuments.includes(id)) {
            options.toIgnore.referenceDocuments.push(id);
            this.addAssociatedToIgnore(dataModel.referenceDocumentsDAO.findItem(id), options);
          }
        }
      }
      if (object.unique_documents) {
        for (const id of object.unique_documents) {
          if (!options.toIgnore.uniqueDocuments.includes(id)) {
            options.toIgnore.uniqueDocuments.push(id);
            this.addAssociatedToIgnore(dataModel.uniqueDocumentsDAO.findItem(id), options);
          }
        }
      }
      if (object.inventory_id) {
        if (!options.toIgnore.inventories.includes(object.inventory_id)) {
          options.toIgnore.inventories.push(object.inventory_id);
          this.addAssociatedToIgnore(dataModel.inventoryDAO.findItem(object.inventory_id), options);
        }
      }
      if (object.delays) {
        for (const id of object.delays) {
          if (!options.toIgnore.delays.includes(id)) {
            options.toIgnore.delays.push(id);
            this.addAssociatedToIgnore(dataModel.delaysDAO.findItem(id), options);
          }
        }
      }
      if (object.teams) {
        for (const id of object.teams) {
          if (!options.toIgnore.teams.includes(id)) {
            options.toIgnore.teams.push(id);
            this.addAssociatedToIgnore(dataModel.teamsDAO.findItem(id), options);
          }
        }
      }
      if (object.announcements) {
        for (const id of object.announcements) {
          if (!options.toIgnore.announcements.includes(id)) {
            options.toIgnore.announcements.push(id);
            this.addAssociatedToIgnore(dataModel.announcementsDAO.findItem(id), options);
          }
        }
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/dao/StoreManager.js